import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardDescription, CardFooter,
  CardHeader, CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { XCircle, ArrowLeft, Undo2 } from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/useAuthSession';

export default function SubscriptionCancelled() {
  const navigate = useNavigate();
  const { session } = useAuthSession();
  const userId = session?.user?.id;

  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [restorablePlan] = useState<'basic' | 'pro' | null>('basic'); // Could be dynamically fetched

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      toast.error("Please enter some feedback before submitting.");
      return;
    }

    if (!userId) {
      toast.error("User not identified. Please log in again.");
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from('subscription_feedback').insert({
        user_id: userId,
        feedback,
        submitted_at: new Date().toISOString()
      });

      if (error) throw error;

      toast.success("Thanks for your feedback!");
      setFeedback('');
    } catch (err) {
      console.error(err);
      toast.error("Could not submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestore = () => {
    if (restorablePlan) {
      navigate(`/subscription/success?plan_id=${restorablePlan}&billing_cycle=monthly&restored=true`);
    }
  };

  return (
    <MainLayout>
      <div className="container max-w-4xl mx-auto py-12">
        <Card className="border-amber-100 bg-gradient-to-b from-amber-50 to-white shadow-md">
          <CardHeader className="pb-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <XCircle className="h-10 w-10 text-amber-600" />
            </div>
            <CardTitle className="text-2xl font-bold">Subscription Cancelled</CardTitle>
            <CardDescription className="text-base">
              Your subscription process has been cancelled.
            </CardDescription>
          </CardHeader>

          <CardContent className="text-center space-y-6">
            <p className="text-muted-foreground">
              You can continue using our free features, or subscribe again when you're ready.
            </p>

            <div className="text-left">
              <label htmlFor="feedback" className="font-medium block mb-1">
                Tell us why you cancelled (optional):
              </label>
              <Textarea
                id="feedback"
                rows={4}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Your feedback helps us improve..."
              />
              <Button
                className="mt-2"
                disabled={submitting}
                onClick={handleSubmitFeedback}
              >
                {submitting ? "Submitting..." : "Submit Feedback"}
              </Button>
            </div>

            {restorablePlan && (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-md p-4">
                <p className="font-medium">Changed your mind?</p>
                <p>You can restore your previous <strong>{restorablePlan}</strong> plan instantly.</p>
                <Button
                  className="mt-3"
                  variant="default"
                  onClick={handleRestore}
                >
                  <Undo2 className="h-4 w-4 mr-2" />
                  Restore Previous Plan
                </Button>
              </div>
            )}
          </CardContent>

          <CardFooter className="flex justify-center gap-4 pb-8">
            <Button
              variant="outline"
              onClick={() => navigate('/subscription/plans')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Plans
            </Button>
            <Button onClick={() => navigate('/dashboard')}>
              Continue to Dashboard
            </Button>
          </CardFooter>
        </Card>
      </div>
    </MainLayout>
  );
}